<script setup lang="ts">
import { computed, ref } from 'vue'
import { Archive, FileText, FolderKanban, Pencil, Plus, Search } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useI18n } from 'vue-i18n'
import { filterProjects, type ProcessProject } from '@/utils/processProjects'

const props = defineProps<{ projects: ProcessProject[]; capabilities: any }>()
const emit = defineEmits<{
  (event: 'open', project: ProcessProject): void
  (event: 'create', value: { name: string; description: string; position: number }): void
  (event: 'update', project: ProcessProject): void
  (event: 'archive', project: ProcessProject): void
}>()
const { t } = useI18n()
const search = ref('')
const creating = ref(false)
const editingId = ref<number | null>(null)
const createForm = ref({ name: '', description: '', position: 0 })
const editForm = ref<ProcessProject | null>(null)
const visibleProjects = computed(() => filterProjects(props.projects, search.value))

function createProject() {
  emit('create', { ...createForm.value })
  createForm.value = { name: '', description: '', position: 0 }
  creating.value = false
}

function beginEdit(project: ProcessProject) {
  editingId.value = project.id
  editForm.value = { ...project }
}

function updateProject() {
  if (!editForm.value) return
  emit('update', { ...editForm.value })
  editingId.value = null
  editForm.value = null
}

function archiveProject(project: ProcessProject) {
  if (window.confirm(t('processManagement.archiveProjectConfirm'))) emit('archive', project)
}
</script>

<template>
  <div class="space-y-5">
    <div class="flex flex-wrap items-center gap-3">
      <div class="relative min-w-64 flex-1">
        <Search class="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
        <Input v-model="search" class="pl-9" :placeholder="t('processManagement.searchProjects')" />
      </div>
      <Button v-if="capabilities.can_edit" @click="creating = !creating"><Plus class="mr-2 h-4 w-4" />{{ t('processManagement.newProject') }}</Button>
    </div>

    <form v-if="creating" class="grid gap-3 rounded-xl border bg-white p-4 shadow-sm md:grid-cols-[1fr_2fr_120px_auto]" @submit.prevent="createProject">
      <div><Label>{{ t('processManagement.projectName') }}</Label><Input v-model="createForm.name" required /></div>
      <div><Label>{{ t('processManagement.projectDescription') }}</Label><Input v-model="createForm.description" /></div>
      <div><Label>{{ t('processManagement.position') }}</Label><Input v-model.number="createForm.position" type="number" /></div>
      <div class="self-end"><Button type="submit">{{ t('processManagement.create') }}</Button></div>
    </form>

    <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      <article v-for="project in visibleProjects" :key="project.id" class="rounded-xl border bg-white p-5 shadow-sm transition hover:border-[#80BA27] hover:shadow-md">
        <form v-if="editingId === project.id && editForm" class="space-y-3" @submit.prevent="updateProject">
          <div><Label>{{ t('processManagement.projectName') }}</Label><Input v-model="editForm.name" required /></div>
          <div><Label>{{ t('processManagement.projectDescription') }}</Label><textarea v-model="editForm.description" rows="3" class="w-full rounded-md border p-2" /></div>
          <div><Label>{{ t('processManagement.position') }}</Label><Input v-model.number="editForm.position" type="number" /></div>
          <div class="flex gap-2"><Button size="sm" type="submit">{{ t('processManagement.save') }}</Button><Button size="sm" type="button" variant="outline" @click="editingId = null">{{ t('view.close') }}</Button></div>
        </form>
        <template v-else>
          <button type="button" class="w-full text-left" @click="emit('open', project)">
            <h2 class="text-lg font-semibold text-slate-900">{{ project.name }}</h2>
            <p class="mt-1 min-h-10 text-sm text-slate-500">{{ project.description || t('processManagement.noProjectDescription') }}</p>
            <div class="mt-4 flex gap-4 text-xs text-slate-500">
              <span class="flex items-center gap-1"><FolderKanban class="h-4 w-4" />{{ project.process_count || 0 }} {{ t('processManagement.processesCount') }}</span>
              <span class="flex items-center gap-1"><FileText class="h-4 w-4" />{{ project.document_count || 0 }} {{ t('processManagement.documentsCount') }}</span>
            </div>
          </button>
          <div v-if="capabilities.can_edit" class="mt-4 flex gap-2 border-t pt-3">
            <Button size="sm" variant="outline" @click="beginEdit(project)"><Pencil class="mr-1 h-3.5 w-3.5" />{{ t('view.edit') }}</Button>
            <Button size="sm" variant="ghost" class="text-red-600" @click="archiveProject(project)"><Archive class="mr-1 h-3.5 w-3.5" />{{ t('processManagement.archive') }}</Button>
          </div>
        </template>
      </article>
    </div>
    <p v-if="!visibleProjects.length" class="rounded-xl border bg-white p-12 text-center text-sm text-slate-500">{{ t('processManagement.noProjects') }}</p>
  </div>
</template>
